import {
  useId,
  useMemo,
  useState,
  type ChangeEvent,
  type ComponentProps,
} from "react";
import type {
  InputBaseComponentProps,
  InputProps as StandardInputProps,
} from "@mui/material";
import {
  Box,
  Divider,
  FormControlLabel,
  TextField as MuiTextField,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { debug as debugFn } from "debug";
import { useField } from "formik";
import { useTranslation } from "react-i18next";

import type { TranslationKeyAndOptions } from "@thalamos/vision-common";
import { translate } from "@thalamos/vision-common";

import { Tooltip } from "../../Tooltip/Tooltip.js";
import { FormLabel } from "../FormLabel/FormLabel.js";

export type TextFieldProps = {
  fieldName: string;
  label?: TranslationKeyAndOptions;
  rows?: string;
  multiline?: boolean;
  canBeUnknown?: boolean;
  InputProps?: Partial<StandardInputProps>;
  inputProps?: InputBaseComponentProps;
  tooltip?: {
    title: TranslationKeyAndOptions;
    content: TranslationKeyAndOptions;
  };
  placeholder?: TranslationKeyAndOptions;
  disabled?: boolean;
  errorMessage?: string;
} & Pick<ComponentProps<typeof MuiTextField>, "type" | "autoFocus">;

type TextFieldOutput =
  | {
      value: string | null;
      isUnknown?: boolean;
    }
  | ""
  | undefined
  | null;

// Determine if the value is defined.
// This is useful because it creates a "type-guard" allowing us to
// destructure the value without worrying about null/undefined
const isDefined = (value: TextFieldOutput) =>
  value !== undefined && value !== null && value !== "";

const debug = debugFn("vision-frontend:TextField");

export function TextField({
  fieldName,
  label,
  rows,
  canBeUnknown,
  multiline,
  tooltip,
  InputProps,
  inputProps,
  placeholder,
  type,
  disabled,
  errorMessage,
  autoFocus,
}: TextFieldProps) {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField<TextFieldOutput>(fieldName);

  const ariaId = useId();
  const mergedInputProps = {
    ...InputProps?.inputProps,
    ...inputProps,
  };
  const mergedInputTestId =
    typeof mergedInputProps["data-testid"] === "string"
      ? mergedInputProps["data-testid"]
      : undefined;

  // We use this to store the last value that the user typed in the text field.
  // This is so that we can restore it if they turn on the "unknown" flag.
  // We pre-populate this with the incoming "initialValue".
  const [lastTypedValue, setLastTypedValue] = useState<string | null>(
    isDefined(field.value) ? field.value.value : null,
  );

  // Clean up the field value to be a structured object
  const fieldValue = useMemo(
    () =>
      isDefined(field.value)
        ? field.value.isUnknown === true
          ? // If "unknown", clear out the value
            { value: null, isUnknown: true }
          : field.value
        : // Turn null-ish values into a structured object
          { value: null, ...(canBeUnknown ? { isUnknown: false } : {}) },
    [canBeUnknown, field.value],
  );

  // The error could either come from Formik ("Invalid input") or from the schema
  // ({value: "string too short"}). We need to figure out which at runtime.
  const fieldError = useMemo(
    () => {
      const value =
        typeof meta.error === "string"
          ? meta.error
          : typeof meta.error === "object"
            ? (meta.error as { value: string } | undefined)?.value
            : undefined;

      return value === undefined ? undefined : t(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(meta.error)],
  );
  if (fieldError) {
    debug("fieldError", fieldError);
  }

  const handleTextChange = (event: ChangeEvent<HTMLInputElement>) => {
    const runAsync = async () => {
      // Store away the thing they typed so we can restore it if they
      // turn on the unknown flag
      setLastTypedValue(event.target.value);

      // We need to treat "" as a special case in order for optional fields to work
      const newValue =
        event.target.value === ""
          ? ""
          : {
              value: event.target.value,
              ...(canBeUnknown ? { isUnknown: false } : {}),
            };

      debug("handleTextChange, setting value", newValue);
      // Skip validation while typing (the `false` overrides the form's
      // validateOnChange). Validating the whole form schema on every keystroke was
      // the dominant performance cost on large forms. The field is marked touched
      // and validated on blur via field.onBlur instead. We also don't setTouched
      // here, so errors don't flash before the user has finished editing.
      await helpers.setValue(newValue, false);
    };

    void runAsync();
  };

  const handleUnknownChange = (event: ChangeEvent<HTMLInputElement>) => {
    const runAsync = async () => {
      const newValue = event.target.checked
        ? {
            value: null,
            isUnknown: true,
          }
        : {
            value: lastTypedValue,
            isUnknown: false,
          };

      debug("handleUnknownChange, Setting value", newValue);

      await helpers.setValue(newValue);
      await helpers.setTouched(true);
    };

    void runAsync();
  };

  return (
    <Stack data-testid={`formField-${fieldName}`}>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          justifyContent: "flex-start",
          marginBottom: "0.5rem",
        }}
      >
        {label && <FormLabel {...label} htmlFor={ariaId} />}
        {tooltip && (
          <Tooltip
            fieldName={fieldName}
            title={tooltip.title}
            content={tooltip.content}
          />
        )}
      </Box>
      <MuiTextField
        placeholder={placeholder ? translate(t, placeholder) : undefined}
        name={fieldName}
        value={fieldValue.value ?? lastTypedValue ?? ""}
        error={meta.touched && Boolean(fieldError) && !disabled}
        multiline={multiline}
        rows={rows}
        type={type}
        autoFocus={autoFocus}
        helperText={
          // We always render the helper text inside a consistent <span> element
          // so the DOM structure never changes when `meta.touched` flips on blur.
          // Without this, the structural change from a text node to a <span>
          // triggers a layout recalculation that can shift elements below the
          // input (e.g. the "Next" button), breaking coordinate-based clicks.
          <Box
            component="span"
            role={meta.touched && !disabled ? "alert" : undefined}
          >
            {meta.touched && !disabled
              ? errorMessage || fieldError || "\u00A0"
              : "\u00A0"}
          </Box>
        }
        onChange={handleTextChange}
        onBlur={field.onBlur}
        disabled={fieldValue.isUnknown}
        fullWidth
        slotProps={{
          input: {
            ...InputProps,
            autoComplete: "off",
            inputProps: {
              ...mergedInputProps,
              id: ariaId,
              "data-testid":
                mergedInputTestId ?? `formField-${fieldName}-input`,
            },
          },
        }}
      />
      {canBeUnknown && (
        <FormControlLabel
          control={
            <Switch
              checked={fieldValue.isUnknown}
              onChange={handleUnknownChange}
              data-testid={`formField-${fieldName}-unknown`}
            />
          }
          label={t("common.unknown")}
        />
      )}
      {debug.enabled && (
        <>
          <Divider />
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"field.value : "}
            {JSON.stringify(field.value)}
          </Typography>
          <Typography sx={{ color: debug.color, fontFamily: "monospace" }}>
            {"Treated like: "}
            {JSON.stringify(fieldValue)}
          </Typography>
          <Divider />
        </>
      )}
    </Stack>
  );
}
